'use client';

import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
} from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Logo } from '@/components/layout/logo';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { appStoreUrl, NAV_LINKS } from '@/lib/constants';
import { cn } from '@/lib/utils';

function MobileMenu({
  onClose,
  pathname,
}: {
  onClose: () => void;
  pathname: string;
}) {
  return createPortal(
    <motion.div
      className="bg-background fixed inset-0 z-[9999] flex flex-col"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
      }}
      initial={{ clipPath: 'circle(0% at 50% 28px)' }}
      animate={{ clipPath: 'circle(200% at 50% 28px)' }}
      exit={{ clipPath: 'circle(0% at 50% 28px)' }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="container flex items-center justify-between py-5">
        <div onClick={onClose}>
          <Logo />
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground py-2 font-mono text-[0.625rem] tracking-wider uppercase transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      <div className="container flex flex-1 flex-col justify-center">
        {NAV_LINKS.map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: 0.15 + i * 0.08,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <Link
              href={item.href}
              onClick={onClose}
              className={cn(
                'flex items-baseline gap-4 border-b border-dashed py-6 no-underline transition-colors',
                pathname === item.href || pathname.startsWith(item.href + '/')
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="text-foreground/40 font-mono text-xs">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="font-display text-4xl tracking-tight">
                {item.label}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="container pb-10">
        <Button asChild size="lg" className="w-full rounded-xl">
          <Link
            href={appStoreUrl('web_nav')}
            target="_blank"
            rel="noopener"
            onClick={onClose}
          >
            Get the app
          </Link>
        </Button>
      </div>
    </motion.div>,
    document.body,
  );
}

const AUTH_ROUTES = ['/login', '/signup'];

export default function Navbar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const isAuthPage = AUTH_ROUTES.includes(pathname);
  const lastY = useRef(0);

  useMotionValueEvent(scrollY, 'change', (y) => {
    const delta = y - lastY.current;
    lastY.current = y;
    // Show at top, hide on scroll down, reveal on scroll up
    if (y < 20) {
      setHidden(false);
      setScrolled(false);
    } else if (delta > 3) {
      setHidden(true);
      setScrolled(true);
    } else if (delta < -3) {
      setHidden(false);
    }
  });

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [open]);

  if (isAuthPage) return null;

  return (
    <>
      <motion.nav
        className={cn(
          'fixed inset-x-0 top-0 z-20 transition-[background-color] duration-300',
          scrolled
            ? 'bg-background/80 border-border/40 border-b backdrop-blur-md'
            : 'bg-transparent',
        )}
        animate={{
          y: hidden ? '-100%' : '0%',
        }}
        transition={{ type: 'spring', stiffness: 200, damping: 30 }}
      >
        <div className="container flex h-16 items-center justify-between md:h-[88px]">
          {/* Logo */}
          <Logo />

          {/* Desktop links + CTA */}
          <div className="hidden items-center gap-6 md:flex lg:gap-7">
            {NAV_LINKS.map((item) => {
              const isActive =
                pathname === item.href ||
                pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={cn(
                    'text-sm font-medium no-underline transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            <Button asChild className="h-10 rounded-xl px-4">
              <Link href={appStoreUrl('web_nav')} target="_blank" rel="noopener">
                Get the app
              </Link>
            </Button>
            <ThemeToggle />
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(true)}
            className="text-muted-foreground hover:text-foreground flex items-center py-2 font-mono text-[0.625rem] tracking-wider uppercase transition-colors md:hidden"
          >
            Menu
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <MobileMenu onClose={() => setOpen(false)} pathname={pathname} />
        )}
      </AnimatePresence>
    </>
  );
}
